import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Collapse,
  Group,
  Loader,
  Progress,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCloudUpload,
  IconExternalLink,
  IconFileText,
  IconFileTypePdf,
  IconFileZip,
  IconPhoto,
  IconSparkles,
  IconTrash,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import AIPayDraftEditor from "./AIPayDraftEditor";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleGenericError } from "../../utils/errorHandler";
import {
  bulkDealImportService,
  isProcessing,
  isUnresolved,
} from "../../services/bulkDealImport";
import {
  draftToEditorState,
  payDraftPayload,
  withBaseTier,
} from "../../services/payExtraction";
import "./BulkDealImportPage.css";

// Bulk deal import: drop a pile of offer letters, let the AI read each one
// (recipient name + draft pay) and guess the rep, then confirm or correct each
// into a real deal — instead of keying them in one at a time. Mirrors the iOS
// BulkDealImportView against /v1/bulk-deal-import/*.

const POLL_MS = 3000;

// PDFs and images the extraction engine can read, plus a .zip bundling many of
// them (the server expands it and re-checks each document).
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.zip";
const ACCEPTED_EXTENSIONS = ACCEPT.split(",");

const STATUS_META = {
  pending: { label: "Queued", color: "gray" },
  extracting: { label: "Reading…", color: "blue" },
  ready: { label: "Needs review", color: "orange" },
  confirmed: { label: "Deal created", color: "teal" },
  skipped: { label: "Discarded", color: "gray" },
  failed: { label: "Couldn't read", color: "red" },
};

const MATCH_META = {
  high: { label: "Confident match", color: "teal" },
  medium: { label: "Likely match", color: "yellow" },
  low: { label: "Weak match", color: "orange" },
  none: { label: "No match — pick a rep", color: "red" },
};

const STEPS = [
  {
    icon: IconCloudUpload,
    title: "Upload",
    body: "Drop offer letters or comp agreements — PDFs, images, or a .zip of many.",
  },
  {
    icon: IconSparkles,
    title: "Reppi reads",
    body: "Each document is read for the recipient, pay terms, and the likely rep.",
  },
  {
    icon: IconUserCheck,
    title: "You confirm",
    body: "Check the rep and pay, then create the deal. Nothing is saved until you do.",
  },
];

// The AI draft as the editor works with it: an empty commission scale still
// shows (and saves) its base tier.
const seedState = draft => {
  const state = draftToEditorState(draft);
  state.commissionLines = withBaseTier(state.commissionLines);
  return state;
};

const repLabel = rep =>
  `${rep.first_name || ""} ${rep.last_name || ""}`.trim() ||
  `Rep ${rep.rep_id}`;

const fileExtension = name => {
  const match = /\.[^.]+$/.exec(name || "");
  return match ? match[0].toLowerCase() : "";
};

const FileIcon = ({ name, size = 20 }) => {
  const ext = fileExtension(name);
  if (ext === ".pdf") return <IconFileTypePdf size={size} />;
  if (ext === ".zip") return <IconFileZip size={size} />;
  if ([".png", ".jpg", ".jpeg", ".gif", ".webp"].includes(ext)) {
    return <IconPhoto size={size} />;
  }
  return <IconFileText size={size} />;
};

const formatBytes = bytes => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

function BulkDealImportPage() {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const fileInputRef = useRef(null);

  const [overview, setOverview] = useState(null);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const [batchId, setBatchId] = useState(null);
  const [items, setItems] = useState([]);
  const [reps, setReps] = useState([]);
  const [recentBatches, setRecentBatches] = useState([]);

  // Per-item review state: the reviewer's rep / guarantor picks and the editable
  // pay draft, seeded from the AI's suggestions the first time an item is ready.
  const [reviews, setReviews] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [busyItemId, setBusyItemId] = useState(null);
  const [itemErrors, setItemErrors] = useState({});

  const guarantorOptions = useMemo(() => {
    const list = Array.isArray(overview?.guarantor_options)
      ? overview.guarantor_options
      : [];
    return list.map(g => {
      const names = (Array.isArray(g.deal_users) ? g.deal_users : [])
        .map(
          u =>
            u.name ||
            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.email ||
            ""
        )
        .filter(Boolean);
      return {
        value: String(g.id),
        label: g.is_company_deal
          ? g.company_name || `Guarantor ${g.id}`
          : names.join(" & ") || g.company_name || `Guarantor ${g.id}`,
        isCompanyDeal: !!g.is_company_deal,
        isCurrentUserDeal: !!g.is_current_user_deal,
      };
    });
  }, [overview]);

  const repOptions = useMemo(
    () =>
      reps.map(rep => ({
        value: String(rep.rep_id),
        label: rep.is_manager ? `${repLabel(rep)} (manager)` : repLabel(rep),
      })),
    [reps]
  );

  const sellingYears = useMemo(
    () =>
      (Array.isArray(overview?.selling_years) ? overview.selling_years : [])
        .slice()
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [overview]
  );

  // The guarantor the AI read off the document, matched against the year's
  // guarantor deals by name; falls back to the reviewer's own deal, then the
  // company deal — the same precedence as the offer flow.
  const defaultGuarantorId = useCallback(
    guarantorName => {
      if (guarantorOptions.length === 0) return null;
      const name = (guarantorName || "").trim().toLowerCase();
      const byName = name
        ? guarantorOptions.find(
            g =>
              g.label.toLowerCase().includes(name) ||
              name.includes(g.label.toLowerCase())
          )
        : null;
      const fallback =
        guarantorOptions.find(g => g.isCurrentUserDeal) ||
        guarantorOptions.find(g => g.isCompanyDeal) ||
        guarantorOptions[0];
      return (byName || fallback).value;
    },
    [guarantorOptions]
  );

  const fetchOverview = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint("/v1/ps/overview"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      const data = result?.data ?? result ?? {};
      setOverview(data);
      if (data.selected_selling_year_id != null) {
        setSelectedYearId(String(data.selected_selling_year_id));
      }
    } catch (err) {
      // The page still works without it — the pickers just lose their options.
    }
  }, [getVerifiedJWT]);

  const fetchRecentBatches = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const data = await bulkDealImportService.listBatches(jwt);
      setRecentBatches(Array.isArray(data) ? data : []);
    } catch (err) {
      setRecentBatches([]);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    fetchOverview();
    fetchRecentBatches();
  }, [fetchOverview, fetchRecentBatches]);

  // Seed the review state for every item the AI has finished reading. Items
  // already reviewed keep their edits — this only fills in new arrivals.
  useEffect(() => {
    setReviews(prev => {
      let changed = false;
      const next = { ...prev };
      items.forEach(item => {
        if (next[item.id] || item.status !== "ready") return;
        next[item.id] = {
          repId:
            item.suggested_rep_id != null
              ? String(item.suggested_rep_id)
              : null,
          guarantorId: defaultGuarantorId(item.pay_draft?.guarantor_name),
          state: seedState(item.pay_draft),
        };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [items, defaultGuarantorId]);

  const loadBatch = useCallback(
    async id => {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const data = await bulkDealImportService.getBatch(id, jwt);
      setBatchId(data.batch_id);
      setItems(Array.isArray(data.items) ? data.items : []);
      setReps(Array.isArray(data.reps) ? data.reps : []);
    },
    [getVerifiedJWT]
  );

  const anyProcessing = items.some(isProcessing);

  // Extraction runs in the background, so the list polls itself until every
  // document has been read.
  useEffect(() => {
    if (!batchId || !anyProcessing) return undefined;
    const timer = setInterval(() => {
      loadBatch(batchId).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [batchId, anyProcessing, loadBatch]);

  // Files arrive from the picker or a drop; both merge into the pending list,
  // skipping types the engine can't read and files already queued.
  const addFiles = incoming => {
    const list = Array.from(incoming || []).filter(f =>
      ACCEPTED_EXTENSIONS.includes(fileExtension(f.name))
    );
    if (list.length === 0) return;
    setFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
      const fresh = list.filter(f => !seen.has(`${f.name}:${f.size}`));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  };

  const removeFile = index =>
    setFiles(prev => prev.filter((_, i) => i !== index));

  const openFilePicker = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    try {
      setUploading(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const data = await bulkDealImportService.createBatch(
        files,
        jwt,
        selectedYearId != null ? Number(selectedYearId) : null
      );
      setBatchId(data.batch_id);
      setItems(Array.isArray(data.items) ? data.items : []);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      // The create response has no rep list — the review dropdowns need it.
      await loadBatch(data.batch_id);
      fetchRecentBatches();
    } catch (err) {
      setError(err.message || "Failed to upload documents");
    } finally {
      setUploading(false);
    }
  };

  const applyItem = updated =>
    setItems(prev => prev.map(i => (i.id === updated.id ? updated : i)));

  const handleConfirm = async item => {
    const review = reviews[item.id];
    if (!review?.repId) {
      setItemErrors(prev => ({ ...prev, [item.id]: "Pick a rep first." }));
      return;
    }
    try {
      setBusyItemId(item.id);
      setItemErrors(prev => ({ ...prev, [item.id]: null }));
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const payload = {
        ...payDraftPayload(review.state),
        rep_id: Number(review.repId),
        ...(review.guarantorId != null
          ? { guarantor_id: Number(review.guarantorId) }
          : {}),
      };
      const data = await bulkDealImportService.confirmItem(
        item.id,
        payload,
        jwt
      );
      applyItem(data.item);
      setExpandedId(null);
      showSuccess("Deal created");
    } catch (err) {
      setItemErrors(prev => ({
        ...prev,
        [item.id]: err.message || "Failed to create the deal",
      }));
    } finally {
      setBusyItemId(null);
    }
  };

  const handleSkip = async item => {
    try {
      setBusyItemId(item.id);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const updated = await bulkDealImportService.skipItem(item.id, jwt);
      applyItem(updated);
      if (expandedId === item.id) setExpandedId(null);
    } catch (err) {
      handleGenericError(err, "Failed to discard the document");
    } finally {
      setBusyItemId(null);
    }
  };

  const patchReview = (itemId, patch) =>
    setReviews(prev => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));

  const processedCount = items.filter(i => !isProcessing(i)).length;
  const unresolvedCount = items.filter(isUnresolved).length;
  const confirmedCount = items.filter(i => i.status === "confirmed").length;
  const closedCount = items.filter(
    i => i.status === "skipped" || i.status === "failed"
  ).length;

  const headerActions = (
    <Button
      variant="outline"
      leftSection={<IconArrowLeft size={16} />}
      onClick={() => navigate("/downline")}
    >
      Back to Reps
    </Button>
  );

  const renderSteps = () => (
    <div className="bdi-steps">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        return (
          <div key={step.title} className="bdi-step">
            <span className="bdi-step-number">{index + 1}</span>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Icon size={16} color="var(--mantine-color-orange-7)" />
                <Text fw={600} size="sm">
                  {step.title}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {step.body}
              </Text>
            </Stack>
          </div>
        );
      })}
    </div>
  );

  const renderUploader = () => (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <div
          className={[
            "bdi-dropzone",
            dragging ? "bdi-dropzone--active" : "",
            uploading ? "bdi-dropzone--disabled" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="button"
          tabIndex={0}
          aria-label="Upload compensation documents"
          onClick={openFilePicker}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openFilePicker();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            if (!dragging) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            if (!uploading) addFiles(e.dataTransfer?.files);
          }}
        >
          <span className="bdi-dropzone-icon">
            <IconCloudUpload size={28} />
          </span>
          <Text fw={700}>
            {dragging
              ? "Drop to add these documents"
              : "Drag & drop compensation documents"}
          </Text>
          <Text size="sm" c="dimmed">
            or{" "}
            <Text span c="orange.7" fw={600}>
              browse your files
            </Text>
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            PDF, PNG, JPG, GIF, WEBP, or a .zip bundling many of them
          </Text>
          <input
            ref={fileInputRef}
            className="bdi-dropzone-input"
            type="file"
            multiple
            accept={ACCEPT}
            disabled={uploading}
            onChange={e => {
              addFiles(e.target.files);
              // Let the same file be picked again after it's been removed.
              e.target.value = "";
            }}
          />
        </div>

        {files.length > 0 && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                {plural(files.length, "document")} ready to read
              </Text>
              <Button
                variant="subtle"
                color="gray"
                size="compact-xs"
                onClick={() => setFiles([])}
                disabled={uploading}
              >
                Clear all
              </Button>
            </Group>
            <div className="bdi-file-list">
              {files.map((file, index) => (
                <div key={`${file.name}:${file.size}`} className="bdi-file-row">
                  <FileIcon name={file.name} />
                  <div className="bdi-file-row-name">
                    <Text size="sm" truncate>
                      {file.name}
                    </Text>
                  </div>
                  <Text size="xs" c="dimmed">
                    {formatBytes(file.size)}
                  </Text>
                  <Tooltip label="Remove" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                </div>
              ))}
            </div>
          </Stack>
        )}

        <Group justify="space-between" align="end" wrap="wrap" gap="sm">
          {sellingYears.length > 0 ? (
            <Select
              label="Season"
              description="Deals are created in this selling year"
              data={sellingYears.map(y => ({
                value: String(y.id),
                label: y.name,
              }))}
              value={selectedYearId}
              onChange={setSelectedYearId}
              allowDeselect={false}
              w={220}
              size="sm"
            />
          ) : (
            <span />
          )}
          <Button
            color="orange"
            leftSection={<IconSparkles size={16} />}
            onClick={handleUpload}
            loading={uploading}
            disabled={files.length === 0 || uploading}
          >
            {files.length > 0
              ? `Read ${plural(files.length, "document")}`
              : "Read documents"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );

  const renderResumable = () => {
    const resumable = recentBatches.filter(
      b => b.batch_id !== batchId && (b.counts?.ready || 0) > 0
    );
    if (resumable.length === 0) return null;
    return (
      <Card withBorder radius="md" p="md">
        <Stack gap={2} mb="sm">
          <Text fw={700} size="sm">
            Pick up where you left off
          </Text>
          <Text size="xs" c="dimmed">
            Earlier batches with documents still waiting on your review.
          </Text>
        </Stack>
        <Stack gap="xs">
          {resumable.map(batch => (
            <div key={batch.batch_id} className="bdi-file-row">
              <span className="bdi-item-icon">
                <IconFileText size={18} />
              </span>
              <div className="bdi-file-row-name">
                <Text size="sm" fw={600} truncate>
                  {plural(batch.total, "document")}
                </Text>
                {batch.created_at && (
                  <Text size="xs" c="dimmed">
                    Uploaded {new Date(batch.created_at).toLocaleDateString()}
                  </Text>
                )}
              </div>
              <Badge color="orange" variant="light">
                {batch.counts?.ready || 0} to review
              </Badge>
              <Button
                size="xs"
                variant="light"
                color="orange"
                onClick={() =>
                  loadBatch(batch.batch_id).catch(err =>
                    setError(err.message || "Failed to open that batch")
                  )
                }
              >
                Open
              </Button>
            </div>
          ))}
        </Stack>
      </Card>
    );
  };

  const renderBatchSummary = () => (
    <Card withBorder radius="md" p="md">
      <Group justify="space-between" wrap="nowrap" align="start" mb="md">
        <Stack gap={2}>
          <Text fw={700}>{plural(items.length, "document")} in this batch</Text>
          <Text size="sm" c="dimmed">
            {anyProcessing
              ? `Reading ${processedCount} of ${items.length}… this page updates on its own.`
              : unresolvedCount > 0
                ? `${unresolvedCount} still need${unresolvedCount === 1 ? "s" : ""} your review.`
                : "Every document has been handled."}
          </Text>
        </Stack>
        <Button
          variant="outline"
          size="sm"
          leftSection={<IconCloudUpload size={16} />}
          onClick={() => {
            setBatchId(null);
            setItems([]);
            setReviews({});
            setExpandedId(null);
            fetchRecentBatches();
          }}
        >
          Import more
        </Button>
      </Group>

      <div className="bdi-stats">
        <div className="bdi-stat">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Documents
          </Text>
          <div className="bdi-stat-value">{items.length}</div>
        </div>
        <div
          className={`bdi-stat${unresolvedCount > 0 ? " bdi-stat--attention" : ""}`}
        >
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Needs review
          </Text>
          <div className="bdi-stat-value">{unresolvedCount}</div>
        </div>
        <div
          className={`bdi-stat${confirmedCount > 0 ? " bdi-stat--done" : ""}`}
        >
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Deals created
          </Text>
          <div className="bdi-stat-value">{confirmedCount}</div>
        </div>
        <div className="bdi-stat">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            Set aside
          </Text>
          <div className="bdi-stat-value">{closedCount}</div>
        </div>
      </div>

      {anyProcessing && (
        <Progress
          mt="md"
          value={items.length ? (processedCount / items.length) * 100 : 0}
          color="orange"
          size="sm"
          radius="xl"
          animated
        />
      )}
    </Card>
  );

  const renderItem = item => {
    const status = STATUS_META[item.status] || {
      label: item.status,
      color: "gray",
    };
    const review = reviews[item.id];
    const expanded = expandedId === item.id;
    const match = MATCH_META[item.match_confidence];
    const busy = busyItemId === item.id;

    return (
      <Card
        key={item.id}
        withBorder
        radius="md"
        p="md"
        className={`bdi-item bdi-item--${item.status}`}
      >
        <Group justify="space-between" wrap="nowrap" align="start" gap="md">
          <Group gap="sm" wrap="nowrap" align="start" style={{ minWidth: 0 }}>
            <span className="bdi-item-icon">
              {isProcessing(item) ? (
                <Loader size={18} color="blue" />
              ) : (
                <FileIcon name={item.original_filename} />
              )}
            </span>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" truncate>
                {item.original_filename}
              </Text>
              <Text size="xs" c="dimmed">
                {item.extracted_name
                  ? `Offer for ${item.extracted_name}`
                  : isProcessing(item)
                    ? "Reading the document…"
                    : "No recipient name found"}
                {item.suggested_rep_name
                  ? ` · matched ${item.suggested_rep_name}`
                  : ""}
              </Text>
              {item.error_message && (
                <Text size="xs" c="red">
                  {item.error_message}
                </Text>
              )}
            </Stack>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {match && item.status === "ready" && (
              <Badge color={match.color} variant="light">
                {match.label}
              </Badge>
            )}
            <Badge color={status.color} variant="light">
              {status.label}
            </Badge>
          </Group>
        </Group>

        {item.status === "ready" && review && (
          <>
            <div className="bdi-review">
              <div className="bdi-review-pickers">
                <Select
                  label="Rep"
                  placeholder="Pick the rep this offer is for"
                  data={repOptions}
                  value={review.repId}
                  onChange={value => patchReview(item.id, { repId: value })}
                  searchable
                  size="sm"
                  error={!review.repId ? "Required to create the deal" : null}
                />
                <Select
                  label="Guarantor"
                  placeholder="Who backs this deal"
                  data={guarantorOptions.map(({ value, label }) => ({
                    value,
                    label,
                  }))}
                  value={review.guarantorId}
                  onChange={value =>
                    patchReview(item.id, { guarantorId: value })
                  }
                  searchable
                  size="sm"
                />
              </div>

              <div className="bdi-review-pay">
                <Group justify="space-between" wrap="wrap" gap="xs">
                  <Button
                    variant={expanded ? "light" : "default"}
                    color="orange"
                    size="xs"
                    rightSection={
                      expanded ? (
                        <IconChevronUp size={14} />
                      ) : (
                        <IconChevronDown size={14} />
                      )
                    }
                    onClick={() => setExpandedId(expanded ? null : item.id)}
                  >
                    {expanded ? "Hide pay details" : "Review pay details"}
                  </Button>
                  {item.document_url && (
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      component="a"
                      href={item.document_url}
                      target="_blank"
                      rel="noreferrer"
                      rightSection={<IconExternalLink size={14} />}
                    >
                      Open original
                    </Button>
                  )}
                </Group>

                <Collapse in={expanded}>
                  <Stack gap="lg" mt="md">
                    <AIPayDraftEditor
                      draft={item.pay_draft}
                      state={review.state}
                      onChange={state => patchReview(item.id, { state })}
                      overview={overview}
                      sellingYearId={
                        selectedYearId != null ? Number(selectedYearId) : null
                      }
                    />
                  </Stack>
                </Collapse>
              </div>
            </div>

            {itemErrors[item.id] && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                mt="sm"
              >
                {itemErrors[item.id]}
              </Alert>
            )}

            <div className="bdi-item-footer">
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconTrash size={14} />}
                onClick={() => handleSkip(item)}
                loading={busy}
              >
                Discard
              </Button>
              <Button
                color="orange"
                size="sm"
                leftSection={<IconCheck size={14} />}
                onClick={() => handleConfirm(item)}
                loading={busy}
                disabled={!review.repId}
              >
                Create deal
              </Button>
            </div>
          </>
        )}

        {item.status === "confirmed" && item.created_deal_id && (
          <div className="bdi-item-footer">
            <Group gap={6}>
              <IconCheck size={16} color="var(--mantine-color-teal-6)" />
              <Text size="sm" c="teal.8">
                Deal created
                {item.suggested_rep_name
                  ? ` for ${item.suggested_rep_name}`
                  : ""}
              </Text>
            </Group>
            <Button
              variant="light"
              color="teal"
              size="xs"
              rightSection={<IconExternalLink size={14} />}
              onClick={() => navigate(`/deals/${item.created_deal_id}`)}
            >
              View deal
            </Button>
          </div>
        )}

        {item.status === "failed" && (
          <div className="bdi-item-footer">
            <Text size="sm" c="dimmed">
              Reppi couldn't read this file. Add the deal manually or discard
              it.
            </Text>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              leftSection={<IconTrash size={14} />}
              onClick={() => handleSkip(item)}
              loading={busy}
            >
              Discard
            </Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <PageContent
      title="Import Deals"
      subtitle="Upload offer letters and let Reppi draft each deal for review"
      breadcrumbs={[
        { label: "Rep Management", path: "/downline" },
        { label: "Import Deals" },
      ]}
      headerActions={headerActions}
    >
      <Stack gap="lg">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {!batchId && renderSteps()}
        {!batchId && renderUploader()}
        {!batchId && renderResumable()}

        {batchId && (
          <>
            {renderBatchSummary()}

            {items.length === 0 ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : (
              <Stack gap="md">{items.map(renderItem)}</Stack>
            )}
          </>
        )}
      </Stack>
    </PageContent>
  );
}

export default BulkDealImportPage;
